import React, { useEffect, useRef, useState } from "react";
import { Loader2, Pen, Type as TypeIcon, Eraser } from "lucide-react";
import closeIcon from "../../assets/cross.svg";
import { studentService } from "../../services/studentService";

// Near-black ink (not pure #000) so it reads like handwriting on the cert.
const INK = "#1a1a2e";

// Curated handwriting fonts for the "Type" tab. Loaded from Google Fonts
// (web app, online) just for the canvas render — the saved artifact is a
// flat PNG, so there's no runtime font dependency afterwards.
const SCRIPT_FONTS = [
  { id: "Dancing Script", weight: 600 },
  { id: "Great Vibes", weight: 400 },
  { id: "Sacramento", weight: 400 },
  { id: "Allura", weight: 400 },
];
const FONT_LINK_ID = "sig-script-fonts";
const FONT_LINK_HREF =
  "https://fonts.googleapis.com/css2?family=Allura&family=Dancing+Script:wght@600&family=Great+Vibes&family=Sacramento&display=swap";

// Crop a canvas to the non-transparent ink + a little padding, return a PNG
// data URL. Trimming keeps the signature from floating with huge margins on
// the certificate's signature line. Returns null when nothing was drawn.
function trimToPngDataUrl(canvas) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  let found = false;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 10) {
        found = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (!found) return null;
  const pad = 10;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  out.getContext("2d").drawImage(canvas, minX, minY, w, h, 0, 0, w, h);
  return out.toDataURL("image/png");
}

const CreateSignatureModal = ({ onClose, onSaved }) => {
  const [tab, setTab] = useState("draw");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Draw tab
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Type tab
  const [typedName, setTypedName] = useState("");
  const [font, setFont] = useState(SCRIPT_FONTS[0].id);

  // Load the script fonts once (for the Type preview + canvas render).
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = FONT_LINK_HREF;
    document.head.appendChild(link);
  }, []);

  // (Re)initialise the drawing context whenever the Draw tab mounts.
  useEffect(() => {
    if (tab !== "draw") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = INK;
  }, [tab]);

  const pointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    canvas.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    const p = pointerPos(e);
    lastRef.current = p;
    // A down-only tap should still leave a dot.
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.arc(p.x, p.y, ctx.lineWidth / 2, 0, Math.PI * 2);
    ctx.fillStyle = INK;
    ctx.fill();
    setHasDrawn(true);
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = pointerPos(e);
    const last = lastRef.current;
    // Quadratic smoothing through the midpoint for a natural stroke.
    const mid = { x: (last.x + p.x) / 2, y: (last.y + p.y) / 2 };
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.quadraticCurveTo(last.x, last.y, mid.x, mid.y);
    ctx.stroke();
    lastRef.current = p;
  };

  const endStroke = () => {
    drawingRef.current = false;
    lastRef.current = null;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    canvas?.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  // Render the typed name into an offscreen transparent canvas in the chosen
  // script font, then trim. Waits for the font so the first render isn't a
  // fallback.
  const renderTypedSignature = async () => {
    const name = typedName.trim();
    if (!name) return null;
    const px = 96;
    await document.fonts.load(`${px}px "${font}"`).catch(() => {});
    const measure = document.createElement("canvas").getContext("2d");
    measure.font = `${px}px "${font}"`;
    const w = Math.ceil(measure.measureText(name).width) + 80;
    const h = Math.ceil(px * 1.8);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.font = `${px}px "${font}"`;
    ctx.fillStyle = INK;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillText(name, w / 2, h / 2);
    return trimToPngDataUrl(canvas);
  };

  const canSave = tab === "draw" ? hasDrawn : typedName.trim().length > 0;

  const handleSave = async () => {
    setError(null);
    let dataUrl = null;
    if (tab === "draw") {
      dataUrl = trimToPngDataUrl(canvasRef.current);
    } else {
      dataUrl = await renderTypedSignature();
    }
    if (!dataUrl) {
      setError(tab === "draw" ? "Draw your signature first." : "Type a name first.");
      return;
    }
    const base64Data = dataUrl.split(",")[1];
    setSaving(true);
    try {
      const updated = await studentService.saveSignatureImage(base64Data);
      onSaved?.(updated);
    } catch (err) {
      setError(err?.message || "Couldn't save the signature.");
    } finally {
      setSaving(false);
    }
  };

  const TabButton = ({ id, icon: Icon, label }) => (
    <button
      type="button"
      onClick={() => {
        setTab(id);
        setError(null);
        // The canvas unmounts on tab switch, so its pixels are gone — reset
        // the draw flag so Save reflects reality when you come back.
        setHasDrawn(false);
      }}
      className={`flex-1 h-10 inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-lg transition-colors ${
        tab === id ? "bg-primary text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      }`}
    >
      <Icon size={15} />
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center" onMouseDown={onClose}>
      <div
        className="w-[560px] max-w-[92vw] bg-white rounded-[20px] overflow-hidden font-['Manrope']"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-6 bg-gradient-to-br from-white to-blue-100 border-b border-blue-100 flex justify-between items-center">
          <h2 className="text-[18px] font-bold text-gray-900">Create a signature</h2>
          <button type="button" onClick={onClose} className="p-1">
            <img src={closeIcon} alt="Close" className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-6 pt-5 flex flex-col gap-4">
          <div className="flex gap-2">
            <TabButton id="draw" icon={Pen} label="Draw" />
            <TabButton id="type" icon={TypeIcon} label="Type" />
          </div>

          {tab === "draw" ? (
            <div className="flex flex-col gap-2">
              <div className="rounded-xl border-2 border-dashed border-gray-200 bg-[repeating-linear-gradient(45deg,#fafafa,#fafafa_10px,#fff_10px,#fff_20px)] relative">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  className="w-full h-[200px] touch-none cursor-crosshair"
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={endStroke}
                  onPointerLeave={endStroke}
                />
                {/* Signature baseline guide */}
                <div className="pointer-events-none absolute left-8 right-8 bottom-12 border-b border-gray-300" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">Sign with your mouse, trackpad, or finger.</span>
                <button
                  type="button"
                  onClick={clearCanvas}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700"
                >
                  <Eraser size={13} />
                  Clear
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <input
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="e.g., Sam Rivera"
                maxLength={40}
                className="h-11 px-3 rounded-lg border border-gray-200 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <div className="flex flex-wrap gap-2">
                {SCRIPT_FONTS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFont(f.id)}
                    style={{ fontFamily: `"${f.id}"`, fontWeight: f.weight }}
                    className={`px-3 h-11 rounded-lg border text-xl text-gray-900 transition-colors ${
                      font === f.id ? "border-primary ring-2 ring-blue-200" : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {typedName.trim() || "Signature"}
                  </button>
                ))}
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 h-[120px] flex items-center justify-center overflow-hidden">
                <span
                  style={{ fontFamily: `"${font}"`, color: INK }}
                  className="text-[44px] leading-none px-4 truncate"
                >
                  {typedName.trim() || "Your signature"}
                </span>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="h-11 px-4 text-[15px] font-medium text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave || saving}
              className="h-11 px-5 text-[15px] font-semibold text-white bg-primary rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : null}
              Save signature
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSignatureModal;
